/**
 * Simplified Dashboard for Viewer and Writer Roles
 * 
 * Provides a simplified, read-only view of key features for users with limited permissions.
 * Shows only the essential flows they need access to.
 */

import React from 'react';
import { PageContainer } from '../components/UI/PageContainer';
import { PageHeader } from '../components/UI/PageHeader';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { usePermissions } from '../hooks/usePermissions';
import { useWorkspace } from '../hooks/useWorkspace';
import { 
  Search, 
  FileText, 
  Eye, 
  BarChart3, 
  Database,
  Lock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export function SimplifiedDashboard() {
  const { hasPermission, hasAnyPermission, permissions, roles, isLoading } = usePermissions();
  const { tenantId, projectId } = useWorkspace();

  // Determine user role level
  const isViewer = hasAnyPermission(['rfp.proposals.view', 'proposals.read']) && 
                   !hasPermission('proposals.write');
  const isWriter = hasPermission('proposals.write') && 
                   !hasPermission('proposals.approve');
  const isAdmin = hasPermission('proposals.approve');
  const isAdminTech = hasPermission('data_management.read');

  // Get role display name
  const roleDisplayName = roles.length > 0 
    ? roles[0].display_name 
    : isViewer ? 'Proposal Viewer' 
    : isWriter ? 'Proposal Writer'
    : isAdmin ? 'Admin'
    : 'User';

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Available features based on permissions
  const availableFeatures = [
    {
      id: 'search',
      label: 'AI Search',
      description: 'Search across proposals, documents, and opportunities',
      icon: Search,
      path: '/search',
      permission: 'documents.read',
      available: hasPermission('documents.read'),
    },
    {
      id: 'proposals',
      label: 'View Proposals',
      description: 'Browse and view proposal documents',
      icon: FileText,
      path: '/rfp/proposals-management?tab=list',
      permission: 'proposals.read',
      available: hasAnyPermission(['proposals.read', 'rfp.proposals.view']),
    },
    {
      id: 'opportunities',
      label: 'View Opportunities',
      description: 'Browse available RFP opportunities',
      icon: Eye,
      path: '/rfp/external-data?tab=opportunities',
      permission: 'opportunities.read',
      available: hasAnyPermission(['opportunities.read', 'rfp.opportunities.view']),
    },
    {
      id: 'reports',
      label: 'View Reports',
      description: 'View generated reports and proposals',
      icon: BarChart3,
      path: '/reports',
      permission: 'reports.read',
      available: hasPermission('reports.read'),
    },
  ].filter(f => f.available);

  // Features that require higher permissions (shown as locked)
  const lockedFeatures = [
    {
      id: 'data-management',
      label: 'Data Pipeline',
      description: 'Manage data sources and knowledge base',
      icon: Database,
      requiredPermission: 'data_management.read',
      available: false,
    },
  ].filter(f => !hasPermission(f.requiredPermission));

  return (
    <PageContainer>
      <PageHeader
        icon={<BarChart3 />}
        title="Dashboard"
        subtitle={`Welcome! You have ${roleDisplayName} access.`}
      />

      {/* Role Info Card */}
      <Card className="mb-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Your Role: {roleDisplayName}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {isViewer && 'You have read-only access to proposals, opportunities, and reports. You can view content but cannot create or edit proposals.'}
              {isWriter && 'You can create and edit proposals, work plans, and technical approaches. You cannot approve or submit proposals.'}
              {isAdmin && 'You can approve proposals, change their status, and manage all proposal-related content.'}
              {isAdminTech && 'You have full access including data management and system configuration.'}
            </p>
            {roles.length > 0 && (
              <div className="mt-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Permissions: </span>
                <span className="text-xs text-gray-600 dark:text-gray-300">
                  {permissions.length} permission{permissions.length !== 1 ? 's' : ''} granted
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Available Features */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Available Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableFeatures.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card key={feature.id} className="hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                    <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {feature.label}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {feature.description}
                    </p>
                    <Button
                      onClick={() => {
                        window.location.href = feature.path;
                      }}
                      className="w-full"
                    >
                      Open {feature.label}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Locked Features (if any) */}
      {lockedFeatures.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Additional Features (Require Higher Permissions)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lockedFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.id} className="opacity-60">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg flex-shrink-0">
                      <Icon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                          {feature.label}
                        </h3>
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">
                        {feature.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                        <AlertCircle className="h-4 w-4" />
                        <span>Requires: {feature.requiredPermission}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Help Section */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
              Need More Access?
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              If you need access to additional features, please contact your administrator to request a role upgrade.
            </p>
          </div>
        </div>
      </Card>
    </PageContainer>
  );
}

